import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAdminUser, useUpdateUser } from "@/lib/api";

interface Props {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserEditDialog({ userId, open, onOpenChange }: Props) {
  const userQuery = useAdminUser(open ? userId : null);
  const updateMutation = useUpdateUser();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("user");
  const [error, setError] = useState("");

  // Reset form fields whenever a different user is loaded.
  useEffect(() => {
    if (userQuery.data) {
      setFirstName(userQuery.data.first_name ?? "");
      setLastName(userQuery.data.last_name ?? "");
      setRole(userQuery.data.role);
      setError("");
    }
  }, [userQuery.data]);

  const handleSave = async () => {
    if (!userId) {
      return;
    }
    setError("");
    try {
      await updateMutation.mutateAsync({ userId, firstName, lastName, role });
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update user.");
    }
  };

  const user = userQuery.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        {userQuery.isError ? (
          <p role="alert" className="text-destructive py-8 text-center text-sm">
            Failed to load user details.
          </p>
        ) : userQuery.isLoading || !user ? (
          <div className="flex justify-center py-8">
            <Loader2 className="text-muted h-6 w-6 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-muted text-sm">{user.email}</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="edit-first-name" className="text-primary text-sm font-medium">
                  First name
                </label>
                <Input
                  id="edit-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="edit-last-name" className="text-primary text-sm font-medium">
                  Last name
                </label>
                <Input
                  id="edit-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label htmlFor="edit-role" className="text-primary text-sm font-medium">
                Role
              </label>
              <select
                id="edit-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="border-input bg-card text-foreground focus-visible:ring-ring h-10 w-full rounded-xl border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {error && (
              <p role="alert" className="text-destructive text-sm">
                {error}
              </p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending || userQuery.isLoading}>
            {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
